if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();
        this.form = this.querySelector('form');
        this.form && this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        // Cart resolution is LAZY (see get cart()). Resolving in the constructor
        // races with <cart-drawer>'s own custom-element upgrade -- if the drawer
        // hasn't connected yet when product-form constructs, this.cart would be
        // null forever and onSubmitHandler line ~150 would hard-redirect to /cart
        // instead of opening the drawer.
        this.submitButton = this.querySelector('[type="submit"]');
        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      get cart() {
        return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        // Authoritative variant resolution at submit time.
        //
        // We DO NOT trust the hidden #id input (server-rendered to the first
        // available variant) or VariantSelects.currentVariant (computed by
        // code that has a history of bugs). Instead we read the user's pick
        // straight from the <select>/<fieldset> DOM right now, match it
        // against product.variants JSON, and write the matched variant id
        // into the hidden input ourselves.
        //
        // This is the single point of truth. Anything upstream can be wrong
        // and the cart will still receive the variant the user actually
        // selected.
        const stopSubmit = (msgKey, fallback) => {
          this.handleErrorMessage(
            (window.variantStrings && window.variantStrings[msgKey]) || fallback
          );
          this.submitButton.classList.remove('loading');
          const spinner = this.querySelector('.loading-overlay__spinner');
          if (spinner) spinner.classList.add('hidden');
        };

        try {
          const sectionEl = this.closest('.shopify-section') ||
                            this.closest('.popup-modal__content__data') ||
                            document;
          const variantSelects = sectionEl && sectionEl.querySelector('variant-selects');

          if (variantSelects) {
            // Read each option-input the user has actually engaged with.
            // For dropdowns we read selectedOptions[0] directly (skipping
            // any .option-empty placeholder). For radio groups we read the
            // :checked input.
            const selects = Array.from(variantSelects.querySelectorAll('select'));
            const fieldsets = Array.from(variantSelects.querySelectorAll('fieldset'));

            let userSelections = null;
            if (selects.length) {
              userSelections = selects.map((s) => {
                const opt = s.selectedOptions && s.selectedOptions[0];
                if (!opt) return null;
                if (opt.classList && opt.classList.contains('option-empty')) return null;
                // Use the <option>'s value attribute (escaped variant value),
                // falling back to textContent if value is empty.
                return (opt.getAttribute('value') || opt.value || opt.textContent || '').trim();
              });
            } else if (fieldsets.length) {
              userSelections = fieldsets.map((fs) => {
                const checked = fs.querySelector('input[type="radio"]:checked');
                return checked ? (checked.value || '').trim() : null;
              });
            }

            if (!userSelections || userSelections.some((v) => v == null || v === '')) {
              stopSubmit('unavailable', 'Please select an option.');
              return;
            }

            // Parse the variants JSON shipped inside <variant-selects>.
            const jsonEl = variantSelects.querySelector('script[type="application/json"]');
            if (!jsonEl) {
              // Without variant data we cannot verify -- refuse rather than
              // silently submitting a possibly-wrong id.
              stopSubmit('unavailable', 'Please select an option.');
              return;
            }
            const variants = JSON.parse(jsonEl.textContent);

            // Match by ALL option strings (handles multi-option products too).
            const matched = variants.find((variant) => {
              if (!Array.isArray(variant.options)) return false;
              if (variant.options.length !== userSelections.length) return false;
              return variant.options.every((opt, i) => String(opt) === String(userSelections[i]));
            });

            if (!matched) {
              stopSubmit('unavailable', 'Please select an option.');
              return;
            }

            if (matched.available === false) {
              stopSubmit('soldOut', 'This variant is sold out.');
              return;
            }

            // Write the authoritative id into the hidden input. This is the
            // only line that controls which variant the cart receives.
            const idInput = this.form.querySelector('input[name="id"]');
            if (idInput) idInput.value = matched.id;
          }
        } catch (e) { /* non-fatal — fall through to normal submission */ }

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading-overlay__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButton.querySelector('span').classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            this.cart.renderContents(response);
          })
          .catch(() => {})
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading-overlay__spinner').classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}

class stickyCartModal extends HTMLElement {
  constructor() {
    super();
    window.addEventListener('scroll', this.update.bind(this));
    this.update();
    this.querySelector('.sticky-cart-button').addEventListener('click', this.onClickHandler.bind(this));
    this.querySelector('.sticky-cart-button').addEventListener('keydown', this.onClickKeydownHandler.bind(this));
  }
  update(){
    var button = document.getElementById(`product-form-${this.dataset.section}`);
    var sect = this.closest('.shopify-section')||this.closest('.popup-modal__content__data');
    var addButton = sect.querySelector('select-option-js')?button.querySelector('.select-options-button'):button.querySelector('[name="add"]');
    const rangeToShowModal = this.getTop(addButton) + addButton.clientHeight;

    if(rangeToShowModal <= 0) return false;
    if(window.scrollY >= rangeToShowModal && !this.classList.contains('show-modal')){
      this.classList.add('show-modal');
      document.querySelector('body').classList.add('modal__sticky-cart');
      document.querySelector('.footer__content').classList.add('sticky-cart-modal_bottom_padding');
    }
    if(window.scrollY < rangeToShowModal && this.classList.contains('show-modal')){
      this.classList.remove('show-modal');
      document.querySelector('body').classList.remove('modal__sticky-cart');
    }
  }
  onClickKeydownHandler(){
    if(event.code.toUpperCase() !== 'ENTER') return false;
    this.onClickHandler();
  }
  onClickHandler(){
    var item = document.querySelector(`.product__column__content [id*="variant-selects-"]`),
        btn_item = document.getElementById("product-container"),
        _headerH = document.querySelector('.shopify-section-group-header-group')?.offsetHeight || 80,
        _y = (item ? this.getTop(item) : this.getTop(btn_item)) - _headerH - 20;
    this.scrollTo(_y,700);
  }
  getTop(el) {
    return el.offsetTop + (el.offsetParent && this.getTop(el.offsetParent));
  }
  scrollTo(to, duration) {
    const element = document.scrollingElement || document.documentElement,
          start = element.scrollTop,
          change = to - start,
    animateScroll = function() {
      element.scrollTop = to;
    };
    animateScroll();
  }
}
customElements.define('sticky-cart-modal', stickyCartModal);
